import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, formatApiErrorDetail } from '@/lib/api';
import { fmtBRL, accountTypeLabel } from '@/lib/format';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Landmark, Trash2, Edit } from 'lucide-react';
import { ACC } from '@/constants/testIds';
import { toast } from 'sonner';

export default function AccountsPage() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', type: 'CORRENTE', initial_balance: 0, active: true });

  const { data } = useQuery({ queryKey: ['accounts'], queryFn: async () => (await api.get('/accounts')).data });

  const saveMut = useMutation({
    mutationFn: async (payload) => editing
      ? (await api.put(`/accounts/${editing.id}`, payload)).data
      : (await api.post('/accounts', payload)).data,
    onSuccess: () => { toast.success('Salvo'); setOpen(false); qc.invalidateQueries({ queryKey: ['accounts'] }); qc.invalidateQueries({ queryKey: ['dashboard'] }); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });
  const delMut = useMutation({
    mutationFn: async (id) => (await api.delete(`/accounts/${id}`)).data,
    onSuccess: () => { toast.success('Removida'); qc.invalidateQueries({ queryKey: ['accounts'] }); qc.invalidateQueries({ queryKey: ['dashboard'] }); },
  });

  const openNew = () => { setEditing(null); setForm({ name: '', type: 'CORRENTE', initial_balance: 0, active: true }); setOpen(true); };
  const openEdit = (a) => { setEditing(a); setForm({ name: a.name, type: a.type, initial_balance: a.initial_balance, active: a.active }); setOpen(true); };

  return (
    <div data-testid={ACC.page} className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-mono">Financeiro</div>
          <h1 className="text-3xl font-heading font-bold tracking-tight">Contas Financeiras</h1>
        </div>
        <Button data-testid={ACC.newBtn} onClick={openNew} className="bg-emerald-600 hover:bg-emerald-700 text-white"><Plus className="h-4 w-4 mr-2" /> Nova Conta</Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {(data || []).map((a) => (
          <Card key={a.id} data-testid={ACC.row(a.id)} className="p-5 card-elevate border-border/70">
            <div className="flex items-start gap-3">
              <div className="h-11 w-11 rounded-lg bg-emerald-500/10 text-emerald-500 grid place-items-center"><Landmark className="h-5 w-5" /></div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-mono">{accountTypeLabel[a.type]}</div>
                <div className="font-semibold truncate">{a.name}</div>
                <div className="mt-2 font-mono tabular-nums text-2xl font-bold">{fmtBRL(a.current_balance)}</div>
                <div className="text-xs text-muted-foreground mt-1">Inicial: {fmtBRL(a.initial_balance)}</div>
              </div>
            </div>
            <div className="flex justify-end gap-1 mt-3">
              <Button size="icon" variant="ghost" onClick={() => openEdit(a)}><Edit className="h-4 w-4" /></Button>
              <Button size="icon" variant="ghost" className="text-rose-500" onClick={() => { if (confirm('Remover conta?')) delMut.mutate(a.id); }}><Trash2 className="h-4 w-4" /></Button>
            </div>
          </Card>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? 'Editar conta' : 'Nova conta'}</DialogTitle></DialogHeader>
          <form onSubmit={(e) => { e.preventDefault(); saveMut.mutate({ ...form, initial_balance: parseFloat(form.initial_balance) }); }} className="space-y-4">
            <div className="space-y-2"><Label>Nome</Label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div className="space-y-2">
              <Label>Tipo</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {['CAIXA', 'BANCO', 'CORRENTE', 'POUPANCA', 'CARTEIRA', 'DIGITAL'].map((t) => <SelectItem key={t} value={t}>{accountTypeLabel[t]}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {!editing && (
              <div className="space-y-2"><Label>Saldo Inicial (R$)</Label><Input type="number" step="0.01" value={form.initial_balance} onChange={(e) => setForm({ ...form, initial_balance: e.target.value })} /></div>
            )}
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setOpen(false)}>Cancelar</Button>
              <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700 text-white" disabled={saveMut.isPending}>Salvar</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
